
import { Fragment, useState, useEffect } from 'react';
import { UserRole, InviteUser } from '@/types';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Select, SelectOption } from '@/components/ui/Select';
import { cn } from '@/lib/utils';
import type { InviteUserResult } from '@/hooks/useUsers';

const roleOptions: SelectOption[] = [
  { value: UserRole.MODERATOR, label: 'Moderator' },
  { value: UserRole.PRODUCER, label: 'Producer' },
  { value: UserRole.ADMIN, label: 'Admin' },
];

export interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InviteUser) => Promise<InviteUserResult>;
  isLoading?: boolean;
  /** When false, hide video/audio permission checkboxes and omit them on save. */
  responsesEnabled?: boolean;
}

export function InviteUserModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  responsesEnabled = true,
}: InviteUserModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: UserRole.MODERATOR,
    canRespondVideo: false,
    canRespondAudio: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorState, setErrorState] = useState<{ message: string; inviteLink?: string } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({ firstName: '', lastName: '', email: '', role: UserRole.MODERATOR, canRespondVideo: false, canRespondAudio: false });
      setErrors({});
      setSuccessMessage(null);
      setErrorState(null);
    }
  }, [isOpen]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setErrorState(null);
    const payload: InviteUser = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      role: formData.role,
      ...(responsesEnabled
        ? {
            canRespondVideo: formData.canRespondVideo,
            canRespondAudio: formData.canRespondAudio,
          }
        : {}),
    };
    const result = await onSubmit(payload);
    if (result.success) {
      setSuccessMessage(result.message);
    } else {
      setErrorState({
        message: result.error,
        inviteLink: result.inviteLink,
      });
    }
  };

  const handleClose = () => {
    setSuccessMessage(null);
    setErrorState(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Fragment>
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={cn(
            'bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full',
            'animate-fade-in'
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="invite-user-title"
        >
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <h2
              id="invite-user-title"
              className="text-lg font-semibold text-slate-900 dark:text-white"
            >
              Invite User
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Send an email invite. They will set their own password via the link.
            </p>
          </div>

          {successMessage ? (
            <div className="px-6 py-4 space-y-3">
              <p className="text-slate-700 dark:text-slate-300">{successMessage}</p>
              <Button onClick={handleClose} className="w-full">
                Done
              </Button>
            </div>
          ) : errorState ? (
            <div className="px-6 py-4 space-y-3">
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-sm font-medium text-red-800 dark:text-red-400">
                  Email could not be sent
                </p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                  {errorState.message}
                </p>
                {errorState.inviteLink && (
                  <div className="mt-3 rounded bg-slate-100 dark:bg-slate-700 p-3">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                      Share this link with the user:
                    </p>
                    <a
                      href={errorState.inviteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 break-all hover:underline"
                    >
                      {errorState.inviteLink}
                    </a>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        navigator.clipboard?.writeText(errorState.inviteLink!);
                      }}
                    >
                      Copy link
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setErrorState(null)} className="flex-1">
                  Try again
                </Button>
                <Button onClick={handleClose} className="flex-1">
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 space-y-4">
                <Input
                  label="First name"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  error={errors.firstName}
                  placeholder="John"
                  required
                />
                <Input
                  label="Last name"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  error={errors.lastName}
                  placeholder="Doe"
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  error={errors.email}
                  placeholder="john@example.com"
                  required
                />
                <Select
                  label="Role"
                  options={roleOptions}
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      role: e.target.value as UserRole,
                    })
                  }
                />
                {responsesEnabled && (
                  <>
                    <Checkbox
                      label="Can respond with video"
                      description="Allow this user to record and send video responses to prayers"
                      checked={formData.canRespondVideo}
                      onChange={(e) =>
                        setFormData({ ...formData, canRespondVideo: e.target.checked })
                      }
                    />
                    <Checkbox
                      label="Can respond with audio"
                      description="Allow this user to record and send audio-only responses to prayers"
                      checked={formData.canRespondAudio}
                      onChange={(e) =>
                        setFormData({ ...formData, canRespondAudio: e.target.checked })
                      }
                    />
                  </>
                )}
              </div>
              <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex gap-3 justify-end">
                <Button type="button" variant="ghost" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Sending…' : 'Send Invite'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Fragment>
  );
}
